/**
 * Authenticated fetch wrapper.
 *
 * - Injects JWT `Authorization: Bearer <token>` from localStorage
 * - Injects `X-Space-Id` from the space store so every space-scoped call
 *   resolves against the right tenancy. Routes that don't care (auth,
 *   admin) ignore it; routes that do, fall back to the user's first
 *   space when it's absent.
 * - Adds `X-Session-Id` on session-scoped requests (production ingress
 *   consistent-hashing)
 * - On 401, clears the token from localStorage and lets the response
 *   propagate. The auth gate in `_app.tsx` re-renders into <LoginPage />
 *   on the next state tick — no `window.location.reload()`, because that
 *   races with in-flight queries and creates a tight 401-reload loop.
 *
 * Pass `apiFetch(path, opts, { crossSpace: true })` to opt out of the
 * `X-Space-Id` injection — used by cross-space catalogs like the
 * sidebar's «История» list and the cross-scope `?scope=all` reads.
 */

import { useSpaceStore } from "@/modules/spaces/space-store";

const SESSION_PATH_RE = /^\/api\/sessions\/([^/?#]+)/;

function extractSessionId(path: string): string | null {
  const m = path.match(SESSION_PATH_RE);
  return m?.[1] ?? null;
}

export interface ApiFetchExtras {
  /** Skip the automatic `X-Space-Id` header — request runs cross-space. */
  crossSpace?: boolean;
}

export async function apiFetch(
  path: string,
  options?: RequestInit,
  extras?: ApiFetchExtras,
): Promise<Response> {
  const token = localStorage.getItem("token");
  const sessionId = extractSessionId(path);
  const spaceId = extras?.crossSpace
    ? null
    : useSpaceStore.getState().currentSpaceId;
  // Hidden model switcher (10-tap the sidebar logo). Backend whitelists
  // accepted values; arbitrary strings are silently ignored.
  const modelOverride = localStorage.getItem("modelOverride");
  const resp = await fetch(path, {
    ...options,
    headers: {
      ...options?.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(sessionId ? { "X-Session-Id": sessionId } : {}),
      ...(spaceId ? { "X-Space-Id": spaceId } : {}),
      ...(modelOverride ? { "X-Model-Override": modelOverride } : {}),
    },
  });
  if (resp.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }
  return resp;
}
